/** Typed wrappers over the memory surface. */
import type { PortalMemory, PortalMemoryKindChoice, PortalMemoryOverview } from '@portal/contracts'
import { apiFetch } from './portal-client'

/** Everything the Memories screen renders: one group per Smith this customer may use. */
export function getMemories(): Promise<PortalMemoryOverview> {
  return apiFetch<PortalMemoryOverview>('/api/memories')
}

function act<T>(id: string, verb: string, body: object): Promise<T> {
  return apiFetch<T>(`/api/memories/${encodeURIComponent(id)}/${verb}`, {
    method: 'POST',
    body: JSON.stringify(body)
  })
}

/**
 * Put one of your own to use.
 *
 * NOT named `useMemory`: a function whose name starts with `use` is a React
 * hook by convention, and the linter reads a call to one inside a handler as a
 * hook called conditionally. The pairing with `stopUsingMemory` reads better
 * anyway.
 */
export function startUsingMemory(id: string, flowId: string, version: number): Promise<{ memory: PortalMemory }> {
  return act(id, 'approve', { flowId, version })
}

/** Decide against one of your own: it closes with the reason, and no Smith applies it. */
export function discardMemory(id: string, flowId: string, version: number, reason: string): Promise<{ closed: true }> {
  return act(id, 'reject', { flowId, version, reason })
}

/** Stop using one of your own that was already in use. */
export function stopUsingMemory(id: string, flowId: string, version: number): Promise<{ closed: true }> {
  return act(id, 'remove', { flowId, version })
}

/** Restate one of your own in your words, which also puts it to use. */
export function rewordMemory(
  id: string,
  flowId: string,
  version: number,
  text: string,
  /** Sent only when it changed; blank takes the title off. */
  title?: string
): Promise<{ memory: PortalMemory }> {
  return act(id, 'edit', { flowId, version, text, ...(title !== undefined ? { title } : {}) })
}

/**
 * Tell a Smith something yourself, instead of waiting for it to notice.
 *
 * The sentence goes through untouched — nothing on either side reads it to
 * decide what you meant — and it belongs to you alone.
 */
export function writeMemory(
  flowId: string,
  text: string,
  kind: PortalMemoryKindChoice,
  title: string
): Promise<{ memory: PortalMemory }> {
  return apiFetch<{ memory: PortalMemory }>('/api/memories', {
    method: 'POST',
    body: JSON.stringify({ flowId, text, kind, title })
  })
}
